import { useLoading } from "../../redux/LoadingContext";
import {
    payloadCreateTransaction,
    payloadLogin,
    payloadProductByLocation,
    payloadRegister,
    payloadRequestOtp,
    payloadResetPassword,
    payloadUpdateUser,
    payloadVerifyOtp
} from "../../utils/models";
import getBaseService from "../base-service/BaseService";


const urlAuth = 'authen';
const urlSms = 'sms';
const urlUser = 'users';
const urlProducts = 'products';
const urlTransactions = 'transactions';
const urlMasterFiles = 'master/files';
const urlMaster = 'master';

export const useApiServices = () => {
    const { setLoading } = useLoading();
    const BaseService = getBaseService(setLoading);

    // ========================================================= //
    // =================== Authentication ====================== //
    // ========================================================= //
    const LoginAsync = async (payload: payloadLogin) => {
        return await BaseService.post(`${urlAuth}/login`, payload);
    };
    const AuthRefreshAsync = async () => {
        return await BaseService.get(`${urlAuth}/refresh`);
    };
    const AuthInfoAsync = async () => {
        return await BaseService.get(`${urlAuth}/info`);
    };
    const RegisterAsync = async (payload: payloadRegister) => {
        return await BaseService.post(`${urlAuth}/register`, payload);
    };
    const ResetPasswordAsync = async (payload: payloadResetPassword) => {
        return await BaseService.post(`${urlAuth}/resetpassword`, payload);
    };

    // ========================================================= //
    // ======================== Sms ============================ //
    // ========================================================= //
    const RequestOtpAsync = async (payload: payloadRequestOtp) => {
        return await BaseService.post(`${urlSms}/request`, payload);
    };
    const VerifyOtpAsync = async (payload: payloadVerifyOtp) => {
        return await BaseService.post(`${urlSms}/verify`, payload);
    };

    // ========================================================= //
    // ======================== User =========================== //
    // ========================================================= //
    const GetUserByIdAsync = async (id: string) => {
        return await BaseService.get(`${urlUser}/${id}`);
    };
    const UpdateUserByIdAsync = async (id: string, payload: payloadUpdateUser) => {
        return await BaseService.put(`${urlUser}/${id}`, payload);
    };
    const DeleteUserByIdAsync = async (id: string) => {
        return await BaseService.delete(`${urlUser}/${id}`);
    };
    const GetUserAsync = async (id: number) => {
        return await BaseService.get(`${urlUser}/${id}`);
    };
    const CreateUserAsync = async (payload: payloadRegister) => {
        return await BaseService.post(`${urlUser}`, payload);
    };

    //products

    const GetProductsAsync = async (id?: string) => {
        return await BaseService.get(`${urlProducts}${id ? `/${id}`: ''}`);
    };

    const GetProductsByLocationAsync = async (payload: payloadProductByLocation) => {
        return await BaseService.post(`${urlProducts}/bylocation`,payload);
    };

    //transaction
    const CreateTransactionAsync = async (payload: payloadCreateTransaction) => {
        return await BaseService.post(`${urlTransactions}`,payload);
    };
    const GetTransactionAsync = async (id?:string) => {
        return await BaseService.get(`${urlTransactions}${id ? `/${id}`: ''}`);
    };
    const CreatePaymentAsync = async (payload: any) => {
        return await BaseService.post(`${urlTransactions}/payment`,payload);
    };

    // ========================================================= //
    // ======================== Omise ========================== //
    // ========================================================= //
    const CreateOmisePromptPayAsync = async (transactionId: string) => {
        return await BaseService.post(`omise/charges/promptpay`, { transactionId });
    };
    const CreateOmiseMobileBankingAsync = async (payload: { transactionId: string; bankCode: string; platformType: string }) => {
        return await BaseService.post(`omise/charges/mobile-banking`, payload);
    };
    const GetOmiseChargeAsync = async (chargeId: string) => {
        return await BaseService.get(`omise/charges/${chargeId}`);
    };
    const CreateOmiseCardChargeAsync = async (payload: { transactionId: string; cardToken: string }) => {
        return await BaseService.post(`omise/charges`, payload);
    };

    //files
    const UploadFileAsync = async (payload: FormData) => {
        return await BaseService.postForm(`${urlMasterFiles}`,payload);
    }
    const GetFileMetaDataAsync = async (id: string) => {
        return await BaseService.get(`${urlMasterFiles}/${id}/metadata`);
    }
    const GetFileContentAsync = async (id: string) => {
        return await BaseService.getFile(`${urlMasterFiles}/${id}/content`);
    }

    const AddAddressAsync = async (euid: string, payload: any) => {
        return await BaseService.post(`${urlMaster}/${euid}/address`, payload);
    }

    const GetAddressesAsync = async (euid: string) => {
        return await BaseService.get(`${urlMaster}/${euid}/address`);
    }

    const UpdateAddressesAsync = async (euid: string, payload: any) => {
        return await BaseService.put(`${urlMaster}/${euid}/address`,payload);
    }

    const GetTaxInvoiceInfosAsync = async (userId: string) => {
        return await BaseService.get(`${urlUser}/${userId}/tax-invoice-infos`);
    }

    const CreateTaxInvoiceInfoAsync = async (userId: string, payload: any) => {
        return await BaseService.post(`${urlUser}/${userId}/tax-invoice-infos`, payload);
    }

    const UpdateTaxInvoiceInfoAsync = async (userId: string, taxInfoId: string, payload: any) => {
        return await BaseService.put(`${urlUser}/${userId}/tax-invoice-infos/${taxInfoId}`, payload);
    }

    const DeleteTaxInvoiceInfoAsync = async (userId: string, taxInfoId: string) => {
        return await BaseService.delete(`${urlUser}/${userId}/tax-invoice-infos/${taxInfoId}`);
    }

    const SetDefaultTaxInvoiceInfoAsync = async (userId: string, taxInfoId: string) => {
        return await BaseService.put(`${urlUser}/${userId}/tax-invoice-infos/${taxInfoId}/set-default`, {});
    }

    // ========================================================= //
    // =================== Notifications ======================= //
    // ========================================================= //
    const GetNotificationsAsync = async () => {
        return await BaseService.get('notifications');
    }
    const GetNotificationUnreadCountAsync = async () => {
        return await BaseService.get('notifications/unread-count');
    }
    const MarkNotificationReadAsync = async (notificationIds: string[]) => {
        return await BaseService.post('notifications/mark-as-read', { NotificationIds: notificationIds });
    }
    const MarkAllNotificationsReadAsync = async () => {
        return await BaseService.post('notifications/mark-all-as-read', {});
    }
    const DeleteNotificationAsync = async (notificationId: string) => {
        return await BaseService.delete(`notifications/${notificationId}`);
    }

    return {
        LoginAsync,
        AuthRefreshAsync,
        AuthInfoAsync,
        RegisterAsync,
        ResetPasswordAsync,
        RequestOtpAsync,
        VerifyOtpAsync,
        GetUserByIdAsync,
        UpdateUserByIdAsync,
        DeleteUserByIdAsync,
        GetUserAsync,
        CreateUserAsync,
        GetProductsAsync,
        GetProductsByLocationAsync,
        CreateTransactionAsync,
        GetTransactionAsync,
        UploadFileAsync,
        GetFileMetaDataAsync,
        GetFileContentAsync,
        CreatePaymentAsync,
        CreateOmisePromptPayAsync,
        CreateOmiseMobileBankingAsync,
        GetOmiseChargeAsync,
        CreateOmiseCardChargeAsync,
        AddAddressAsync,
        GetAddressesAsync,
        UpdateAddressesAsync,
        GetTaxInvoiceInfosAsync,
        CreateTaxInvoiceInfoAsync,
        UpdateTaxInvoiceInfoAsync,
        DeleteTaxInvoiceInfoAsync,
        SetDefaultTaxInvoiceInfoAsync,
        GetNotificationsAsync,
        GetNotificationUnreadCountAsync,
        MarkNotificationReadAsync,
        MarkAllNotificationsReadAsync,
        DeleteNotificationAsync
    };
};
